import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import type { ReactNode } from "react"

const STORAGE_KEY = "c456-color-scheme"

export type ColorScheme = "system" | "light" | "dark"

/** 与 `document.documentElement` 上 `.dark` 的切换逻辑一致，供不直接读 DOM 的组件使用 */
export function isDarkForScheme(scheme: ColorScheme): boolean {
  if (scheme === "dark") {
    return true
  }
  if (scheme === "light") {
    return false
  }
  if (typeof globalThis.matchMedia !== "function") {
    return false
  }
  return globalThis.matchMedia("(prefers-color-scheme: dark)").matches
}

type ColorSchemeContextValue = {
  scheme: ColorScheme
  setScheme: (s: ColorScheme) => void
  ready: boolean
}

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null)

function useColorSchemeState() {
  const [scheme, setScheme] = useState<ColorScheme>("system")
  const [ready, setReady] = useState(false)

  useLayoutEffect(() => {
    if (globalThis.localStorage == null) {
      return
    }
    const stored = globalThis.localStorage.getItem(
      STORAGE_KEY,
    ) as ColorScheme | null
    const initial =
      stored === "light" || stored === "dark" || stored === "system"
        ? stored
        : "system"
    setScheme(initial)
    document.documentElement.classList.toggle("dark", isDarkForScheme(initial))
    setReady(true)
  }, [])

  useLayoutEffect(() => {
    if (!ready || globalThis.localStorage == null) {
      return
    }
    globalThis.localStorage.setItem(STORAGE_KEY, scheme)
    document.documentElement.classList.toggle("dark", isDarkForScheme(scheme))
  }, [scheme, ready])

  useLayoutEffect(() => {
    if (scheme !== "system" || !ready) {
      return
    }
    const mql = globalThis.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => {
      document.documentElement.classList.toggle("dark", mql.matches)
    }
    mql.addEventListener("change", onChange)
    return () => {
      mql.removeEventListener("change", onChange)
    }
  }, [scheme, ready])

  return useMemo(
    () => ({ scheme, setScheme, ready }),
    [scheme, ready],
  )
}

export function ColorSchemeProvider({ children }: { children: ReactNode }) {
  const value = useColorSchemeState()
  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  )
}

export function useColorScheme(): ColorSchemeContextValue {
  const v = useContext(ColorSchemeContext)
  if (v == null) {
    throw new Error("useColorScheme 必须在 ColorSchemeProvider 内使用")
  }
  return v
}
